'use client'

import airbridge from 'airbridge-web-sdk-loader'
import {
  EventOption,
  WaitCallback,
} from 'airbridge-web-sdk-loader/build/airbridge_api'
import { getCookie, setCookie } from 'cookies-next'
import { isUndefined } from 'lodash-es'
import { useCallback, useEffect, useMemo, useState } from 'react'

import type { TAirbridgeObject } from '@/shared/api'
import { isIPad } from '@/shared/lib'

import { OpenDeeplinkOption } from './context'

const IOS_APP_STORE_URL = 'https://apps.apple.com/us/app/id1491007143'

const initializeAirbridge = ({
  onInitialized,
}: {
  onInitialized: () => void
}) => {
  if (airbridge.isSDKEnabled) {
    onInitialized()

    return
  }

  const app = process.env.NEXT_PUBLIC_AIRBRIDGE_APP_NAME
  const webToken = process.env.NEXT_PUBLIC_AIRBRIDGE_WEB_TOKEN

  if (!app || !webToken) {
    return
  }

  airbridge.init(
    {
      app,
      webToken,
      useProtectedAttributionWindow: true,
      protectedAttributionWindowInMinutes: 4320,
      utmParsing: true,
    },
    error => {
      if (!error) {
        onInitialized()
      }
    },
  )
}

export const useAirbridge = () => {
  const [isInitialized, setIsInitialized] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined' || isInitialized) {
      return
    }

    initializeAirbridge({
      onInitialized: () => {
        if (!getCookie('ABID')) {
          airbridge.fetchResource(({ browser: { clientID } }) => {
            if (!clientID) return
            void setCookie('ABID', clientID)
          })
        }
        setIsInitialized(true)
      },
    })
  }, [isInitialized])

  const setUser = useCallback((userId: string) => {
    if (!airbridge.isSDKEnabled) {
      return
    }

    airbridge.setUserID(userId)
  }, [])

  const clearUser = useCallback(() => {
    if (!airbridge.isSDKEnabled) {
      return
    }

    airbridge.clearUser()
  }, [])

  const sendEvent = useCallback(
    ({
      event,
      params,
      onSuccess,
    }: {
      event: string
      params: EventOption
      onSuccess?: WaitCallback
    }) => {
      if (!airbridge.isSDKEnabled) {
        return
      }

      airbridge.events.send(event, params)

      if (onSuccess) {
        airbridge.events.wait(3000, onSuccess)
      }
    },
    [],
  )

  const sendAirbridgeObject = useCallback((object: TAirbridgeObject) => {
    if (!airbridge.isSDKEnabled) {
      return
    }

    const {
      category,
      action,
      label,
      value,
      semanticAttributes,
      customAttributes,
    } = object

    airbridge.events.send(category, {
      ...(!isUndefined(action) && { action }),
      ...(!isUndefined(label) && { label }),
      ...(!isUndefined(value) && { value }),
      ...(semanticAttributes && { semanticAttributes }),
      ...(customAttributes && { customAttributes }),
    } as EventOption)
  }, [])

  const openDeeplink = useCallback(
    (openDeeplinkOption?: Partial<OpenDeeplinkOption>) => {
      if (!airbridge.isSDKEnabled) {
        return
      }

      const DEFAULT_DEEPLINK_OPTIONS: OpenDeeplinkOption = {
        type: 'click',
        deeplinks: {
          ios: 'spacev33m2://app?type=gh',
          android: 'spacev33m2://app?type=gh',
          desktop: `${window.location.origin}/guest/main`,
        },
        fallbacks: {
          ios: 'itunes-appstore',
          android: 'google-play',
        },
      }

      const merged = { ...DEFAULT_DEEPLINK_OPTIONS, ...openDeeplinkOption }

      // Airbridge SDK는 iPadOS 13+ Safari의 UA(Macintosh...)를 데스크탑으로 분류해
      // desktop fallback URL로 이동시킵니다. SDK에 플랫폼을 강제할 수 있는 옵션이 없어
      // iPad를 직접 감지한 뒤 Airbridge를 우회하고 iOS 딥링크를 수동으로 처리합니다.
      if (isIPad()) {
        const iosDeeplink = merged.deeplinks?.ios ?? 'spacev33m2://app?type=gh'

        // 앱이 미설치된 경우 커스텀 스킴은 조용히 실패하므로,
        // 1500ms 후 App Store로 이동하는 타이머를 예약합니다.
        const timer = setTimeout(() => {
          window.location.href = IOS_APP_STORE_URL
        }, 1500)

        // 앱이 열리면 탭이 백그라운드로 전환되어 document.hidden이 true가 됩니다.
        // 이 경우 App Store 이동 타이머를 취소합니다.
        document.addEventListener(
          'visibilitychange',
          () => {
            if (document.hidden) clearTimeout(timer)
          },
          { once: true },
        )

        window.location.href = iosDeeplink

        return
      }

      airbridge.openDeeplink(merged)
    },
    [],
  )

  const values = useMemo(
    () => ({
      isInitialized,
      setUser,
      clearUser,
      sendEvent,
      sendAirbridgeObject,
      openDeeplink,
    }),
    [
      isInitialized,
      setUser,
      clearUser,
      sendEvent,
      sendAirbridgeObject,
      openDeeplink,
    ],
  )

  return values
}
